import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';
import { renderPdfElements, PdfElement } from '../../pdfHtmlImageUtils';

interface MethodologyPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 16,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraphContainer: {
    marginBottom: 14,
  },
  subSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2563EB', // Bright blue headings
    marginBottom: 4,
    marginTop: 16,
  },
  subSectionMeta: {
    fontSize: 9,
    color: '#9CA3AF',
    marginBottom: 12,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
    marginTop: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 28,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 10,
    fontSize: 11.5,
    color: '#4B5563',
  },
  bulletText: {
    flex: 1,
    fontSize: 11.5,
    color: '#1F2937',
    lineHeight: 1.60,
  },
});

function parseHtmlToElementsSync(htmlString: string): PdfElement[] {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNode = (node: Node, container: PdfElement[] = elements, activeStyles = ''): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized, style: activeStyles || undefined });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      let newStyles = activeStyles;
      const styleAttr = el.getAttribute('style') || '';
      if (styleAttr) {
        if (/text-decoration(-line)?:\s*underline/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
        }
        if (/text-decoration(-line)?:\s*line-through/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
        }
        if (/font-weight:\s*(bold|700)/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
        }
        if (/font-style:\s*italic/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
        }
      }

      switch (el.tagName.toLowerCase()) {
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children, newStyles);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'strong':
        case 'b': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'em':
        case 'i': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'u': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        default: {
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, newStyles);
          }
          break;
        }
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }

  return elements;
}

const MethodologyPageV2: React.FC<MethodologyPageProps> = ({ reportData, sectionId }) => {
  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const defaultWebTestCases = [
    { cat: 'Information Gathering', checks: 'Service fingerprinting, exposed files/directories, HTTP methods, metadata disclosure.' },
    { cat: 'Authentication', checks: 'Credential brute-forcing, MFA bypass, password policy, password-reset flaws.' },
    { cat: 'Session Management', checks: 'Cookie flags (Secure/HttpOnly), token entropy, session hijacking, CSRF.' },
    { cat: 'Authorization', checks: 'Broken object level authorization (IDOR), privilege escalation, forced browsing.' },
    { cat: 'Input Validation', checks: 'Injection attacks (SQLi, XSS, Command Injection), XXE, SSRF, unsafe file uploads.' },
    { cat: 'Business Logic', checks: 'Workflow bypass, parameter tampering, race conditions, transaction manipulation.' },
    { cat: 'Configuration', checks: 'Security headers, TLS/SSL configuration, verbose errors, exposed administrative portals.' },
    { cat: 'API Security', checks: 'Excessive data exposure, mass assignment, broken function level authorization.' }
  ];

  const defaultNetworkTestCases = [
    { cat: 'Discovery & enumeration', checks: 'Host sweep, port scan, service/version detection, OS fingerprinting' },
    { cat: 'Service vulnerabilities', checks: 'SMB (MS17-010, signing), RDP, SSH, web services, databases, printers' },
    { cat: 'Credential attacks', checks: 'Default/weak passwords, password spraying, hash cracking, reuse' },
    { cat: 'Active Directory', checks: 'Kerberoasting, AS-REP roasting, LLMNR/NBT-NS poisoning, delegation, ACL abuse' },
    { cat: 'Lateral movement', checks: 'Pass-the-Hash, PsExec/WMI, SMB relay' },
    { cat: 'Privilege escalation', checks: 'Local privesc, misconfigured services, GPO abuse' },
    { cat: 'Configuration & patching', checks: 'Outdated software/known CVEs, SNMP, TLS/SSL, default config' },
    { cat: 'Segmentation', checks: 'VLAN/firewall traversal between zones' }
  ];

  let parsedConfig: any = {};
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {
        parsedConfig = {};
      }
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig?.methodologyV2 || {};

  const getPerspective = () => {
    const testingType = (reportData.testing_type || '').toLowerCase();
    if (testingType.includes('black')) return 'black-box';
    if (testingType.includes('white')) return 'white-box';
    if (testingType.includes('grey') || testingType.includes('gray')) return 'grey-box';
    const boxMatch = testingType.match(/(\w+-box)/);
    if (boxMatch) return boxMatch[1];
    return 'grey-box';
  };

  const getMethodologySentence = (meth: any) => {
    let obj: any = {};
    if (typeof meth === 'object' && meth !== null) {
      obj = meth;
    } else if (typeof meth === 'string' && meth.trim().startsWith('{')) {
      try {
        obj = JSON.parse(meth);
      } catch (_) {}
    }
    
    const perspective = getPerspective();
    const parts = [];
    if (obj.web !== false) parts.push('web application penetration testing');
    if (obj.network === true) parts.push('infrastructure and network penetration testing');
    if (obj.sourceCode === true) parts.push('source code security review');
    
    if (parts.length === 0) {
      return `The assessment followed a ${perspective} penetration testing approach aligned to industry security standards and best practices.`;
    }
    
    const scopeStr = parts.join(', ').replace(/,([^,]*)$/, ' and$1');
    return `The assessment was conducted as a ${perspective} security assessment covering ${scopeStr}, aligned to the OWASP Web Security Testing Guide (WSTG) and mapped to the OWASP Top 10 (2021).`;
  };

  const getLegacyMethodologyString = (meth: any) => {
    if (typeof meth === 'string') {
      if (meth.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(meth);
          if (parsed && (parsed.web !== undefined || parsed.network !== undefined || parsed.mobile !== undefined || parsed.sourceCode !== undefined)) {
            return '';
          }
        } catch (_) {}
      }
      return meth;
    }
    if (typeof meth === 'object' && meth !== null) {
      const keys = Object.keys(meth);
      if (keys.some(key => key.length > 50)) {
        return keys.join(', ');
      }
    }
    return '';
  };

  const legacyString = getLegacyMethodologyString(reportData.methodology);

  let rawApproachHtml = '';
  if (config && config.approach) {
    rawApproachHtml = config.approach;
  } else if (legacyString) {
    rawApproachHtml = legacyString;
  } else if (reportData.methodology) {
    rawApproachHtml = `<p>${getMethodologySentence(reportData.methodology)}</p>`;
  }

  if (!rawApproachHtml) {
    rawApproachHtml = isNetwork
      ? `<p>Testing followed <strong>PTES</strong> and <strong>NIST SP 800-115</strong>, with techniques mapped to <strong>MITRE ATT&CK</strong>. Exploitation was performed carefully to avoid service disruption; findings were manually verified.</p>`
      : `<p>Testing followed the <strong>OWASP Web Security Testing Guide (WSTG)</strong> and was mapped to the <strong>OWASP Top 10 (2021)</strong>. A combination of automated scanning and in-depth manual testing was used; all findings were manually verified to confirm real impact and eliminate false positives.</p>`;
  }

  const summaryElements = parseHtmlToElementsSync(rawApproachHtml);

  const defaultWebPhases = [
    { bold: 'Reconnaissance & information gathering', normal: '' },
    { bold: 'Enumeration & vulnerability identification', normal: '' },
    { bold: 'Manual exploitation & validation', normal: ' - confirming real impact and removing false positives' },
    { bold: 'Post-exploitation & privilege escalation', normal: ' - where in scope' },
    { bold: 'Reporting & remediation guidance', normal: '' }
  ];

  const defaultNetworkPhases = [
    { bold: 'Host discovery', normal: ' - live host identification across in-scope ranges' },
    { bold: 'Port & service enumeration', normal: '' },
    { bold: 'Vulnerability identification', normal: ' (authenticated + unauthenticated)' },
    { bold: 'Exploitation & validation', normal: '' },
    { bold: 'Post-exploitation, lateral movement & Active Directory attacks', normal: '' },
    { bold: 'Privilege escalation to domain dominance', normal: '' },
    { bold: 'Reporting & remediation guidance', normal: '' }
  ];

  const defaultPhases = isNetwork ? defaultNetworkPhases : defaultWebPhases;
  const phasesBullets = Array.isArray(config.phases) ? config.phases : defaultPhases;

  const renderSummaryParagraph = (elements: PdfElement[]) => {
    return elements.map((el, index) => {
      if (el.type === 'paragraph') {
        return (
          <Text key={index} style={{ lineHeight: 1.60, color: '#475569', fontSize: 11.5, marginBottom: 12 }}>
            {el.children.map((child: any, childIdx: number) => {
              if (child.type === 'text') {
                const isBold = child.style && child.style.split(' ').includes('bold');
                return (
                  <Text key={childIdx} style={isBold ? { fontWeight: 'bold', color: '#1E293B' } : {}}>
                    {child.content}
                  </Text>
                );
              } else if (child.type === 'break') {
                return '\n';
              }
              return null;
            })}
          </Text>
        );
      } else if (el.type === 'text') {
        const isBold = el.style && el.style.split(' ').includes('bold');
        return (
          <Text key={index} style={[{ lineHeight: 1.60, color: '#475569', fontSize: 11.5, marginBottom: 12 }, isBold ? { fontWeight: 'bold', color: '#1E293B' } : {}]}>
            {el.content}
          </Text>
        );
      }
      return null;
    });
  };

  const testCases = isNetwork 
    ? defaultNetworkTestCases 
    : (Array.isArray(config.testCases) ? config.testCases : defaultWebTestCases);

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Title */}
      <Text style={styles.sectionTitle}>3. Methodology</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      {/* Overview Paragraph */}
      <View style={styles.paragraphContainer}>
        {renderSummaryParagraph(summaryElements)}
      </View>

      {/* Engagement Phases Bullets */}
      <View style={{ marginBottom: 12 }}>
        {phasesBullets.map((b: any, idx: number) => (
          <View key={idx} style={styles.bulletRow}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>
              <Text style={{ fontWeight: 'bold', color: '#1F2937' }}>{b.bold}</Text>
              {b.normal ? <Text style={{ color: '#4B5563' }}>{b.normal}</Text> : null}
            </Text>
          </View>
        ))}
      </View>

      {/* Subtitle continuing on same page */}
      <View style={{ marginTop: 16 }}>
        <Text style={styles.subSectionTitle}>Test cases covered (high-level)</Text>
      <Text style={styles.subSectionMeta}>
        Summary of the checks performed during this engagement. High-level overview, not an exhaustive per-test log.
      </Text>

      {/* High-Level Checks Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: '30%' }]}>CATEGORY</Text>
          <Text style={[styles.tableHeaderCell, { width: '70%' }]}>HIGH-LEVEL CHECKS</Text>
        </View>
        {testCases.map((tc: any, idx: number) => {
          const isLast = idx === testCases.length - 1;
          return (
            <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
              <Text style={[styles.tableRowCell, { width: '30%', fontWeight: 'bold', color: '#1F2937' }]}>
                {tc.cat}
              </Text>
              <Text style={[styles.tableRowCell, { width: '70%', color: '#374151' }]}>
                {tc.checks}
              </Text>
            </View>
          );
        })}
      </View>
      </View>
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default MethodologyPageV2;
